import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Text } from '@ps-design-system/components/Text';
import { Divider } from '@ps-design-system/components/Divider';
import { Button } from '@ps-design-system/components/Button';
import { Info } from '../shared-components/icons';
import { Profile } from '../../../api/api-profile';

export const VerifyBankAccount = ({
  accessToken,
  userEmail,
  bankAccountUpdateSession
}) => {
  const [status, setStatus] = useState(bankAccountUpdateSession?.status);
  const [attempts, setAttempts] = useState(
    'NOT_INITIATED' === status ? 5
      : 5 - bankAccountUpdateSession?.authorization_attempts || 0);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailExpiration, setEmailExpiration] = useState(bankAccountUpdateSession?.token_expires_at);
  const [timeRemaining, setTimeRemaining] = useState({
    expired: false,
    minutes: 0
  });
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if ('PENDING' === status && emailExpiration) {
      const calculateTimeRemaining = () => {
        const difference = new Date(emailExpiration) - new Date();
        if (0 >= difference) {
          return { expired: true, minutes: 0 };
        }

        const totalMinutes = difference / 60000;
        const minutes = Math.floor(totalMinutes);
        const seconds = Math.floor((totalMinutes - minutes) * 60);

        if (0 < seconds) {
          return { expired: false, minutes: minutes + 1 };
        }

        return { expired: false, minutes: minutes };
      };

      setTimeRemaining(calculateTimeRemaining());

      const interval = setInterval(() => {
        const remaining = calculateTimeRemaining();
        setTimeRemaining(remaining);

        if (remaining.expired) {
          if (0 === attempts) {
            setStatus('LOCKED');
          }
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [status, emailExpiration]);

  const handleSendEmailClick = useCallback(() => {
    setSendingEmail(true);
    setErrorMsg('');
    Profile.sendBankVerificationEmail(accessToken, (response) => {
      if (response?.bankAccountUpdateSession) {
        setEmailExpiration(response.bankAccountUpdateSession?.token_expires_at);
        setAttempts(attempts - 1);
        setStatus(response.bankAccountUpdateSession?.status);
      } else {
        setErrorMsg(
          <FormattedMessage
            defaultMessage="Email could not be sent, please refresh and try again. If error persists, please {link}."
            id="hosting.verify_bank_account.error"
            values={{
              email: userEmail,
              link: (
                <Button
                  appearance="destructive"
                  variant="text-only"
                  className="tw-lowercase tw-caption-2 tw-underline"
                  href="https://support.peerspace.com/en/"
                >
                  <FormattedMessage defaultMessage="Contact support" id="hosting.verify_bank_account.contact_support" />
                </Button>
              )
            }}
          />
        );
      }
      setSendingEmail(false);
    });
  }, [attempts]);

  const subheaderText = useMemo(() => {
    return 'NOT_INITIATED' === status ? <FormattedMessage defaultMessage="Verify your request" id="hosting.verify_bank_account.subheader_verify_your_request" />
      : 'PENDING' === status && !timeRemaining.expired ? <FormattedMessage defaultMessage="Email sent!" id="hosting.verify_bank_account.subheader_email_sent" />
        : 'PENDING' === status && timeRemaining.expired ? <FormattedMessage defaultMessage="Your verification link has expired" id="hosting.verify_bank_account.subheader_link_expired" />
          : <FormattedMessage defaultMessage="Bank updates are locked" id="hosting.verify_bank_account.subheader_updates_locked" />;
  }, [timeRemaining, status, attempts]);

  const mainText = useMemo(() => {
    return 'NOT_INITIATED' === status ?
      <FormattedMessage
        defaultMessage="For security purposes, we will send a secure link to your email address {email}. This link will expire in 15 minutes, and must be clicked to submit your request to update your bank account. Need help? {link}."
        id="hosting.verify_bank_account.security_purposes"
        values={{
          email: <strong>{userEmail}</strong>,
          link: (
            <Button
              variant="underline"
              href="https://support.peerspace.com/en/"
            >
              <FormattedMessage defaultMessage="Contact support" id="hosting.verify_bank_account.contact_support" />
            </Button>
          )
        }}
      />
      : 'PENDING' === status && !timeRemaining.expired ?
        <FormattedMessage
          defaultMessage="We sent you a secure link to confirm it’s you. Click the link to access and update your bank information. This link is valid for {availableFor}. You have {remainingAttempts}."
          id="hosting.verify_bank_account.link_sent"
          values={{
            availableFor: (<strong><FormattedMessage defaultMessage="{minutesNum} minutes" id="hosting.verify_bank_account.minutes" values={{ minutesNum: timeRemaining.minutes }} /></strong>),
            remainingAttempts: (<strong><FormattedMessage defaultMessage="{attempts} remaining attempts" id="hosting.verify_bank_account.remaining_attempts" values={{ attempts }} /></strong>)
          }}
        />
        : 'PENDING' === status && timeRemaining.expired ?
          <FormattedMessage
            defaultMessage="Unfortunately your verification link expired. To verify your identity, we need to send you a new secure link to your email address. You have {remainingAttempts} before access to banking updates on your account is locked."
            id="hosting.verify_bank_account.link_expired"
            values={{
              remainingAttempts: (<strong><FormattedMessage defaultMessage="{attempts} remaining attempts" id="hosting.verify_bank_account.remaining_attempts" values={{ attempts }} /></strong>)
            }}
          />
          : <FormattedMessage defaultMessage="For your security we’ve blocked access to your banking information after 5 unsuccessful verification attempts. Please contact Peerspace support to update your bank account." id="hosting.verify_bank_account.updates_locked" />;
  }, [attempts, status, timeRemaining]);

  const infoText = useMemo(() => {
    return 'NOT_INITIATED' === status ? <FormattedMessage defaultMessage="You have a limit of five verification attempts." id="hosting.verify_bank_account.verification_attempts" />
      : <FormattedMessage
        defaultMessage="Don’t see an email? Check your spam folder or {link}."
        id="hosting.verify_bank_account.dont_see_email"
        values={{
          link: (
            <Button
              className="tw-lowercase"
              variant="underline"
              href="https://support.peerspace.com/en/"
            >
              <FormattedMessage defaultMessage="Contact support" id="hosting.verify_bank_account.contact_support" />
            </Button>
          )
        }}
      />;
  }, [status]);

  const CTAButton = useMemo(() => {
    return 'NOT_INITIATED' === status ? (
      <Button
        appearance="purple"
        fullWidth
        size="large"
        showSpinner={sendingEmail}
        onClick={handleSendEmailClick}
      >
        <FormattedMessage defaultMessage="Send email" id="hosting.verify_bank_account.send_email" />
      </Button>
    ) : 0 < attempts ? (
      <Button
        appearance={timeRemaining.expired ? 'purple' : 'white'}
        variant={timeRemaining.expired ? 'default' : 'outline-2'}
        fullWidth
        size="large"
        showSpinner={sendingEmail}
        onClick={handleSendEmailClick}
      >
        <FormattedMessage defaultMessage="Resend email" id="hosting.verify_bank_account.resend_email" />
      </Button>
    ) : (
      <Button
        appearance="white"
        variant="outline-2"
        fullWidth
        size="large"
        href="https://support.peerspace.com/en/"
      >
        <FormattedMessage defaultMessage="Contact support" id="hosting.verify_bank_account.contact_support" />
      </Button>
    );
  }, [status, attempts, timeRemaining, sendingEmail]);

  return (
    <div className="VerifyBankAccount ps-container-lg-margin ps-container-fixed">
      <div className="ps-row">
        <div className="col-sm-12 col-md-9 col-lg-8 col-xxl-7">
          <Text
            typeface="headline-4-b"
            className="tw-mt-32 tw-block"
          >
            <FormattedMessage defaultMessage="Bank account update" id="hosting.verify_bank_account.header_bank_account_update" />
          </Text>
          <Text
            typeface="headline-3-b"
            className="subheader tw-mt-40 tw-block"
          >
            {subheaderText}
          </Text>
          <Divider spacing={0} className="divider" />
          <Text className={'LOCKED' !== status ? 'tw-mb-16' : 'tw-mb-40'}>
            {mainText}
          </Text>
          {'LOCKED' !== status && (
            <div className="tw-flex tw-gap-6 tw-items-center tw-mb-40">
              <Info width={20} height={20} />
              <Text>{infoText}</Text>
            </div>
          )}
          {errorMsg && (
            <Text typeface="caption-2" className="tw-text-red-800 tw-mb-16">
              {errorMsg}
            </Text>
          )}
          {CTAButton}
          <Button
            className="cancel tw-mt-16"
            variant="underline-m"
            fullWidth
            href="/account/methods"
          >
            {'NOT_INITIATED' === status ? (
              <FormattedMessage defaultMessage="Cancel" id="hosting.verify_bank_account.cancel" />
            ) : (
              <FormattedMessage defaultMessage="Back to payments" id="hosting.verify_bank_account.back_to_payments" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

VerifyBankAccount.propTypes = {
  accessToken: PropTypes.string,
  userEmail: PropTypes.string,
  bankAccountUpdateSession: PropTypes.oneOfType([
    PropTypes.shape({
      status: PropTypes.oneOf(['NOT_INITIATED']).isRequired
    }),
    PropTypes.shape({
      status: PropTypes.oneOf(['PENDING']).isRequired,
      authorization_attempts: PropTypes.number.isRequired,
      token_expires_at: PropTypes.string.isRequired
    }),
    PropTypes.shape({
      status: PropTypes.oneOf(['LOCKED']).isRequired
    })
  ])
};
